import React, { useEffect, useMemo, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { ReportBaseLayout } from './ReportBaseLayout'
import { useReport } from './ReportContext/ReportProvider'
import { UPDATE_FILTER } from '../Cases/Search/SearchContext/ActionTypes'
import * as qs from 'qs'
import { useLazyQuery } from '@apollo/client'
import {
  AggregationVariables,
  GetAggregation,
  GET_AGGREGATION,
} from '../Dashboard/Queries'
import {
  FISCAL_MONTH_YEAR,
  RESOLUTION_FISCAL_MONTH_YEAR,
  FILTER_IDS,
} from './ReportConstants'
import { convertToCurrency, sortByMonthYear } from './ReportsUtils'
import { ColDef } from 'ag-grid-community'
import uniqBy from 'lodash/fp/uniqBy'
import {
  addEscapeChar,
  buildReportQuery,
  buildReportUrl,
} from './ReportContext/ReportUtils'
import {
  Divider,
  ExpandableSection,
  Input,
  Heading,
} from '@enterprise-ui/canvas-ui-react'
import { aggregatedReports } from './ReportContext/AggregatedReportInitalValues'
import { Report, SideFilter, Value } from './ReportContext/ReportContext'

interface ReportData {
  gridRows: any[]
  gridColumns: ColDef[]
  chartData?: any[]
  uniqueSubTypes: string[]
}

interface ReportSwitchProps {
  id: string
  reportTypes: Report[]
  consolidateType?: string
}

const ReportSwitch: React.FC<ReportSwitchProps> = ({
  id,
  reportTypes,
  consolidateType,
}) => {
  const navigate = useNavigate()
  const location = useLocation()
  const reportTypeValues = useMemo(
    () =>
      reportTypes.map((report) => {
        return { value: report.id, label: report.reportName }
      }),
    [reportTypes],
  )

  const consolidateTypes = [
    { value: 'COUNT', label: 'Count' },
    { value: 'AMOUNT', label: 'Amount' },
  ]

  return (
    <>
      <ExpandableSection padding="dense" startExpanded toggleLocation="left">
        <Heading className="hc-clr-black" size={6}>
          Switch Reports
        </Heading>
        <ExpandableSection.Content className="hc-ml-sm hc-mr-sm">
          <div className="hc-mb-dense">
            <Input.Label> Select Report By</Input.Label>
            <Input.Select
              value={id}
              id={'reportType'}
              defaultValue={id}
              options={reportTypeValues}
              onUpdate={(id: any, value: any) => {
                navigate(
                  '/reports/aggregated_reports/' + value + location.search,
                )
              }}
            />
          </div>
          <Divider />
          <Input.Radio
            id="Aging Interval"
            value={consolidateType}
            options={consolidateTypes}
            onUpdate={(id: any, value: any) => {
              navigate(
                `${location.pathname}?` +
                  buildReportUrl(location, {
                    filter: 'consolidateType',
                    event: value,
                    type: 'consolidateType',
                  }),
              )
            }}
          />
        </ExpandableSection.Content>
      </ExpandableSection>
      <Divider />
    </>
  )
}

export const AggregatedReport = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const [state, dispatch] = useReport()
  const [reportData, setReportData] = useState<ReportData | undefined>()
  const [dynamicSideFilters, setDynamicSideFilters] = useState<
    SideFilter[] | undefined
  >(undefined)
  const [processedDataCache, setProcessedDataCache] = useState<any>(null)

  const [loadAggregation, { loading }] = useLazyQuery<
    GetAggregation,
    AggregationVariables
  >(GET_AGGREGATION, {
    fetchPolicy: 'network-only',
    onCompleted: (aggregationDetails) => {
      const aggregation = [...(aggregationDetails.getAggregation ?? [])]

      // Process subType entries - consolidate entries with subRootType into single rows
      const allSubTypes: any[] = []
      const subReasonEntriesMap: Map<string, any[]> = new Map() // Map of rootType -> array of sub entries

      aggregation.forEach((agg: any) => {
        agg.subType?.forEach((sub: any) => {
          if (sub.subRootType) {
            // This entry has sub reason codes - collect separately for consolidation
            const rootType = sub.rootType
            if (!subReasonEntriesMap.has(rootType)) {
              subReasonEntriesMap.set(rootType, [])
            }
            subReasonEntriesMap.get(rootType)!.push({
              ...sub,
              monthYear: agg.rootType, // Keep track of which month this belongs to
            })
          } else if (!sub.subRootType) {
            // Regular entries without subRootType - add directly
            allSubTypes.push({
              ...sub,
              displayName: sub.rootType,
              aggregation: sub.rootType,
              subRootType: null,
            })
          }
        })
      })

      // Add consolidated entries for all reason codes that have sub reason codes
      subReasonEntriesMap.forEach((entries, rootType) => {
        allSubTypes.push({
          rootType: rootType,
          displayName: rootType,
          aggregation: rootType,
          subRootType: null,
          type: state.report?.consolidateType,
          subReasonData: entries, // Store all sub reason code details
        })
      })

      const uniqueRows = uniqBy((item: any) =>
        item.subRootType
          ? `${item.rootType}-${item.subRootType}`
          : item.rootType,
      )(allSubTypes).map((item: any) => item.displayName)

      const uniqueColumns = aggregation
        .sort(sortByMonthYear)
        .map((column) => column.rootType)

      // Build Sub Reason Code filter values from aggregation (any entry with subRootType)
      const subReasonSet = new Set<string>()
      aggregation.forEach((timeEntry: any) => {
        ;(timeEntry?.subType || []).forEach((subTypeEntry: any) => {
          // Extract any entry that has a valid subRootType (not specific to any reason code)
          if (
            subTypeEntry?.subRootType &&
            typeof subTypeEntry.subRootType === 'string' &&
            subTypeEntry.subRootType.trim() !== '' &&
            subTypeEntry.subRootType.toLowerCase() !== 'null'
          ) {
            subReasonSet.add(subTypeEntry.subRootType)
          }
        })
      })

      const subReasonValues: Value[] = Array.from(subReasonSet).map(
        (subRootType) => ({
          id: subRootType,
          value: subRootType,
          label: subRootType,
          isChecked: false,
        }),
      )

      // Store the processed data for later grid generation
      const processedData = {
        allSubTypes,
        uniqueRows,
        uniqueColumns,
        aggregation,
      }

      // Cache processed data for regeneration when filters change
      setProcessedDataCache(processedData)

      // Generate grid data - no filtering, return all rows
      const initialGridData = generateGridData(processedData, [], [])

      // Prepare and pass updated side filters down via state on render
      setDynamicSideFilters((_) => {
        const baseFilters: SideFilter[] = state.report?.sideFilters ?? []
        const updated = baseFilters.map((f) =>
          f.id === FILTER_IDS.SUB_REASON_CODE
            ? { ...f, values: subReasonValues, source: 'dynamic' as const }
            : f,
        )
        return updated
      })

      const chartData = aggregation.sort(sortByMonthYear).map((value) => {
        let subCategory: any = {}
        value.subType?.forEach((type) => {
          subCategory[type['rootType'] as string] =
            state.report?.consolidateType === 'COUNT'
              ? type['value']
              : type['value']?.toFixed(2)
        })
        subCategory.x_axis_data = value.rootType
        subCategory.total = value.value
        return subCategory
      })

      // set grid data
      setReportData({
        gridRows: initialGridData,
        gridColumns: [
          {
            field: 'aggregation',
            headerName: state.id.includes('resolution')
              ? 'Aggregation/ Resolved Month, Year'
              : 'Aggregation/ Filed Month, Year',
            onCellClicked: (props) => {
              navigate(
                `/cases?&search%5B0%5D=${buildDrillDownUrl(props.value)}`,
              )
            },
            sortable: true,
          },
          ...uniqueColumns.map((colum) => {
            let col: ColDef = {
              field: colum,
              onCellClicked: (props) => {
                navigate(
                  `/cases?&search%5B0%5D=${buildDrillDownUrl(
                    props.data.aggregation,
                    props.colDef.field,
                  )}`,
                )
              },
              valueFormatter:
                state.report?.consolidateType === 'AMOUNT'
                  ? (params) => {
                      return `${convertToCurrency(params.value)}`
                    }
                  : undefined,
            }

            return col
          }),
        ],
        chartData: chartData,
        uniqueSubTypes: uniqueRows,
      })
    },
  })

  useEffect(() => {
    dispatch({
      type: UPDATE_FILTER,
      payload: qs.parse(location.search.replace('?', '')),
    })
  }, [dispatch, location.search])

  useEffect(() => {
    handleQueryChange(buildReportQuery(state) ?? '')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state])

  const generateGridData = (
    processedData: any,
    activeSubReasonFilters: string[],
    activeReasonCodeFilters: string[] = [],
  ) => {
    const { allSubTypes, uniqueRows, uniqueColumns, aggregation } =
      processedData

    // Filter uniqueRows by active reason codes if any
    let filteredRows =
      activeReasonCodeFilters.length > 0
        ? uniqueRows.filter((displayName: string) =>
            activeReasonCodeFilters.includes(displayName),
          )
        : uniqueRows

    // If sub reason codes are selected, filter to show only rows that have those sub reason codes
    if (activeSubReasonFilters.length > 0) {
      filteredRows = filteredRows.filter((displayName: string) => {
        const subTypeEntry = allSubTypes.find(
          (sub: any) => sub.displayName === displayName,
        )

        // Only include rows that have subReasonData with matching sub reason codes
        if (subTypeEntry?.subReasonData) {
          const subReasonCodes: string[] = Array.from(
            new Set(
              subTypeEntry.subReasonData.map((entry: any) => entry.subRootType),
            ),
          )
          return subReasonCodes.some((code) =>
            activeSubReasonFilters.includes(code),
          )
        }

        // Exclude rows without sub reason data when sub reason filter is active
        return false
      })
    }

    return filteredRows.map((displayName: string) => {
      let gridRow: any = {
        aggregation: displayName,
      }

      // Find the corresponding subType entry for this display name
      const subTypeEntry = allSubTypes.find(
        (sub: any) => sub.displayName === displayName,
      )

      // Add subReasonCodes field and raw data if this row has sub reason data
      if (subTypeEntry?.subReasonData) {
        const uniqueSubReasonCodes = Array.from(
          new Set(
            subTypeEntry.subReasonData.map((entry: any) => entry.subRootType),
          ),
        )
        gridRow.subReasonCodes = uniqueSubReasonCodes
        // Store raw sub reason data for recalculation in frontend
        gridRow._subReasonData = subTypeEntry.subReasonData
      }

      uniqueColumns.forEach((column: string | undefined) => {
        if (!column) return
        const monthAgg = (aggregation as any[]).find(
          (aggs: any) => aggs.rootType === column,
        )
        let value = 0

        if (subTypeEntry?.subReasonData) {
          // This entry has sub reason codes - filter by active sub reason codes if any
          let subReasonEntriesForMonth = subTypeEntry.subReasonData.filter(
            (entry: any) => entry.monthYear === column,
          )

          // Apply sub reason filter if active
          if (activeSubReasonFilters.length > 0) {
            subReasonEntriesForMonth = subReasonEntriesForMonth.filter(
              (entry: any) =>
                activeSubReasonFilters.includes(entry.subRootType),
            )
          }

          value = subReasonEntriesForMonth.reduce(
            (sum: number, entry: any) => sum + (entry.value || 0),
            0,
          )
        } else {
          // For regular entries without sub reasons, find by rootType only
          value =
            monthAgg?.subType?.find(
              (sub: any) => sub.rootType === displayName && !sub.subRootType,
            )?.value ?? 0
        }

        gridRow[column as string] = value
      })

      return gridRow
    })
  }

  const handleQueryChange = (query: string) => {
    loadAggregation({
      variables: {
        aggregateBy: [
          `${
            state.report?.id !== 'resolution_code_text.keyword'
              ? FISCAL_MONTH_YEAR
              : RESOLUTION_FISCAL_MONTH_YEAR
          },${state.report?.id}`,
        ],
        consolidateType: state.report?.consolidateType,
        query: query,
        numberOfPartitions: state.partitions,
      },
    })
  }

  const buildDrillDownUrl = (
    row: string,
    column?: string,
    isGraph: boolean = false,
  ): string => {
    const query: any = qs.parse(location.search.replace('?', ''))
    const dateFilter = !isGraph
      ? `(${state.report?.dateFilter.id}: [${state.report?.dateFilter.active?.from} TO ${state.report?.dateFilter.active?.to}])`
      : ''

    const sideFilters = Object.keys(query)
      .filter((key) => key !== 'consolidateType')
      .map((key) => {
        if (query[key] instanceof Array) {
          const filters = query[key]
            .map((filter: string) => `(${addEscapeChar(filter)})`)
            .join(' OR ')
          return `(${key}: ${filters})`
        } else {
          return ''
        }
      })
      .filter((value) => value !== '')
      .join(' AND ')

    const idFilter = `(${state.report?.id}: (${addEscapeChar(row)}))`
    const fisicalCalander =
      column !== undefined ? `(${FISCAL_MONTH_YEAR}: (${column}))` : ''
    var arr = [sideFilters, dateFilter, idFilter, fisicalCalander]
      .filter((value: string) => !(value === '') && !value.includes('()'))
      .join(' AND ')

    return arr
  }

  if (state.report == null) {
    return <div>Not Found</div>
  }

  return (
    <ReportBaseLayout
      reportName={state.report.reportName}
      sideFilters={dynamicSideFilters ?? state.report.sideFilters}
      data={reportData?.gridRows ?? []}
      columns={reportData?.gridColumns ?? []}
      chartData={reportData?.chartData ?? []}
      uniqueSubTypes={reportData?.uniqueSubTypes ?? []}
      reportType={'aggregation'}
      dynamicFilter={state.report.dynamicFilter}
      handleQueryChange={handleQueryChange}
      loading={loading}
      dateFilter={state.report.dateFilter}
      customSideFilters={
        <ReportSwitch
          id={state.id}
          reportTypes={aggregatedReports}
          consolidateType={state.report.consolidateType}
        />
      }
      buildDrillDownUrl={buildDrillDownUrl}
      onSubReasonFilterChange={(
        activeSubReasonFilters: string[],
        activeReasonCodeFilters: string[],
      ) => {
        // Regenerate grid data from cached processedData with both filters
        if (processedDataCache) {
          const newGridData = generateGridData(
            processedDataCache,
            activeSubReasonFilters,
            activeReasonCodeFilters,
          )
          setReportData((prevData) =>
            prevData
              ? {
                  ...prevData,
                  gridRows: newGridData,
                }
              : undefined,
          )
        }
      }}
    />
  )
}
